import random from 'lodash/random';
import range from 'lodash/range';
import { memo, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { BankLogoImage } from 'shared/components';
import { noValue } from 'shared/constants';
import { useGetAmaxPayOpenBankingBankAccounts } from 'shared/data/webApp/v1';
import { useCompanyBasedOnUrl } from 'shared/hooks/useCompanyBasedOnUrl';

import { mapMaxPayOpenBankingBankAccount } from '../../helpers/mapMaxPayOpenBankingBankAccount';
import { useMatchXeroPopupQueryParams } from '../../hooks/useMatchXeroPopupQueryParams';
import { BankAccountCard } from '../BankAccountCard/BankAccountCard';
import { NoBankAccountsPlaceholder } from '../NoBankAccountsPlaceholder/NoBankAccountsPlaceholder';
import { Root } from './BankAccounts.styles';

export const BankAccounts = memo(() => {
    const { companyId } = useParams<{ companyId: string }>();
    const { data, isLoading } = useGetAmaxPayOpenBankingBankAccounts({ path: { companyId } });
    const { setBankAccountId, setConsentId } = useMatchXeroPopupQueryParams();
    const company = useCompanyBasedOnUrl();
    const noXeroIntegrationConnected = !company.flags.hasActiveIntegration;

    const items = useMemo(() => (data?.items || []).map(mapMaxPayOpenBankingBankAccount), [data?.items]);

    const normalizedBankAccounts = useMemo(
        () =>
            items.map((bankAccount) => ({
                ...bankAccount,
                matched: Boolean(bankAccount.xeroBankAccount?.id),
                connected: bankAccount.isConnected,
                bankName: bankAccount.bank.name,
                logoImage: (
                    <BankLogoImage src={bankAccount.bank.iconUrl} alt={bankAccount.bank.name} width={40} height={40} />
                ),
                xeroBankAccount: bankAccount.xeroBankAccount
                    ? {
                          ...bankAccount.xeroBankAccount,
                          sortCode: bankAccount.xeroBankAccount.sortCode ?? noValue,
                      }
                    : undefined,
            })),
        [items]
    );

    const handleConnectXeroBankAccount = useCallback(
        (id: string, consentId: string) => {
            setBankAccountId(id);
            setConsentId(consentId);
        },
        [setBankAccountId, setConsentId]
    );

    if (isLoading) {
        return (
            <Root>
                {range(0, random(3, 8)).map((index) => (
                    <BankAccountCard.Loading key={index} />
                ))}
            </Root>
        );
    }

    return normalizedBankAccounts.length ? (
        <Root role='list'>
            {normalizedBankAccounts.map(({ xeroBankAccount, ...banksAccount }) => (
                <BankAccountCard
                    key={banksAccount.id}
                    {...banksAccount}
                    onRematchXeroBankAccount={() =>
                        handleConnectXeroBankAccount(banksAccount.id, banksAccount.consentId)
                    }
                    xeroAccount={
                        noXeroIntegrationConnected ? (
                            <BankAccountCard.XeroIntegrationDisconnectedNotice />
                        ) : xeroBankAccount ? (
                            <BankAccountCard.XeroAccountDetails
                                {...xeroBankAccount}
                                bankAccountConnected={banksAccount.connected}
                            />
                        ) : (
                            <BankAccountCard.XeroAccountConnectRequest
                                onConnectXeroBankAccount={() =>
                                    handleConnectXeroBankAccount(banksAccount.id, banksAccount.consentId)
                                }
                            />
                        )
                    }
                />
            ))}
        </Root>
    ) : (
        <NoBankAccountsPlaceholder />
    );
});

BankAccounts.displayName = 'BankAccounts';
